import React, { useState, useEffect, useRef } from 'react';
import { Play, X, Maximize2 } from 'lucide-react';
import { portfolioVideos } from '../data/mock';

// High-quality thumbnail (usa maior tamanho disponível)
const thumb = (id) => `https://drive.google.com/thumbnail?id=${id}&sz=w2000`;

// URL DIRETA do vídeo original (sem compressão do preview do Drive)
const directVideo = (id) =>
  `https://drive.usercontent.google.com/download?id=${id}&export=view`;

// Fallback iframe (caso o <video> não consiga tocar)
const embedFallback = (id) => `https://drive.google.com/file/d/${id}/preview`;

export default function PortfolioVideos() {
  const [active, setActive] = useState(null);
  const [useFallback, setUseFallback] = useState(false);
  const videoRef = useRef(null);
  const wrapperRef = useRef(null);

  // ESC fecha, trava scroll
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setActive(null);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [active]);

  // Reset fallback ao trocar de vídeo
  useEffect(() => {
    setUseFallback(false);
  }, [active]);

  const openFullscreen = () => {
    const el = wrapperRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  };

  return (
    <section
      id="portfolio"
      className="py-24 lg:py-32 bg-[color:var(--brown-800)] border-y border-[color:var(--gold-500)]/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="text-center mb-16">
          <h2 className="font-serif text-5xl md:text-6xl text-[color:var(--cream-100)]">
            Portfólio
          </h2>
          <h3 className="mt-6 font-serif italic text-4xl md:text-5xl text-[color:var(--cream-100)] leading-tight">
            <span className="underline-gold-lg-dark">Vídeos</span>
          </h3>
          <p className="mt-8 text-[color:var(--cream-100)]/85">
            <span className="underline-gold">Clique em cada vídeo para assistir.</span>
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 lg:gap-10">
          {portfolioVideos.map((v) => (
            <button
              key={v.id}
              onClick={() => setActive(v)}
              className="text-left focus:outline-none group"
            >
              <div className="phone-frame">
                <div className="phone-notch"></div>
                <div className="phone-screen">
                  <img
                    src={thumb(v.driveId)}
                    alt={`Vídeo ${v.id}`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.background = '#111';
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/30 transition-colors">
                    <span className="w-16 h-16 rounded-full bg-white/25 backdrop-blur-sm border border-white/40 flex items-center justify-center group-hover:scale-110 group-hover:bg-white/40 transition-all">
                      <Play size={22} className="text-white ml-1" fill="white" />
                    </span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Modal HD */}
      {active && (
        <div
          className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setActive(null)}
        >
          <button
            onClick={() => setActive(null)}
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 z-10"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              openFullscreen();
            }}
            className="absolute top-4 left-4 z-10 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[color:var(--gold-500)] text-[color:var(--brown-900)] text-[11px] tracking-widest-2 uppercase font-medium hover:bg-[color:var(--gold-400)] transition-colors"
          >
            <Maximize2 size={14} />
            Assistir em tela cheia
          </button>

          <div
            ref={wrapperRef}
            className="relative rounded-2xl overflow-hidden shadow-2xl bg-black"
            style={{
              width: 'min(96vw, calc((100vh - 40px) * 9 / 16), 600px)',
              aspectRatio: '9 / 16',
              maxHeight: '92vh',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {!useFallback ? (
              <video
                ref={videoRef}
                key={active.driveId}
                src={directVideo(active.driveId)}
                controls
                autoPlay
                playsInline
                preload="auto"
                controlsList="nodownload"
                className="absolute inset-0 w-full h-full bg-black"
                style={{ objectFit: 'contain' }}
                onError={() => setUseFallback(true)}
              >
                Seu navegador não suporta vídeos HTML5.
              </video>
            ) : (
              <iframe
                key={active.driveId + '-iframe'}
                src={embedFallback(active.driveId)}
                title={`Vídeo ${active.id}`}
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
}
